import { Etiquette } from '@incubateur-ademe/impactco2-react';
import { shuffle } from 'lodash';
import { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import { computeCo2g, TRANSPORT_MODES_EMISSIONS } from '../../services/co2';
import { TransportId } from '../../services/co2/types';
import ComparisonBox from './box';
import BoxActions from './boxActions';
import messages from './messages';
import { Text, TextHiglight } from './text';
import { TripComparatorProps } from './types';

const EtiquetteWrapper = styled.div`
  ${tw`m-auto w-full`}
  max-width: 396px
`;

const OPTIONS = [
  TransportId.TGV,
  TransportId.THERMAL_BUS,
  TransportId.THERMAL_CAR,
  TransportId.PLANE,
];

const ReduceEmissionsTab = (props: TripComparatorProps & { intl: any }) => {
  const { destinationCity, distanceAsCrowFlies, intl, originCity } = props;

  const [firstSelectValue, setFirstSelectValue] = useState<string>(
    TransportId.TGV,
  );
  const [secondSelectValue, setSecondSelectValue] = useState<string>(
    TransportId.THERMAL_CAR,
  );

  const firstSelectValueCo2g = computeCo2g(
    distanceAsCrowFlies,
    firstSelectValue,
  );
  const secondSelectValueCo2g = computeCo2g(
    distanceAsCrowFlies,
    secondSelectValue,
  );

  const savedCo2g = secondSelectValueCo2g - firstSelectValueCo2g;
  const savedCo2gPercentage = (savedCo2g / secondSelectValueCo2g) * 100;

  const handleFirstSelectChange = (value: string) => {
    if (value === secondSelectValue) {
      setSecondSelectValue(firstSelectValue);
    }
    setFirstSelectValue(value);
  };

  const handleSecondSelectChange = (value: string) => {
    if (value === firstSelectValue) {
      setFirstSelectValue(secondSelectValue);
    }
    setSecondSelectValue(value);
  };

  return (
    <div>
      <ComparisonBox
        firstSelectOnChange={handleFirstSelectChange}
        firstSelectOptions={OPTIONS.map((option) => ({
          children: `${
            TRANSPORT_MODES_EMISSIONS[option].emoji
          } ${intl.formatMessage(messages[option])}`,
          value: option,
        }))}
        firstSelectValue={firstSelectValue}
        middleMessage={<FormattedMessage {...messages.insteadOf} />}
        secondSelectOnChange={handleSecondSelectChange}
        secondSelectOptions={OPTIONS.map((option) => ({
          children: `${
            TRANSPORT_MODES_EMISSIONS[option].emoji
          } ${intl.formatMessage(messages[option])}`,
          value: option,
        }))}
        secondSelectValue={secondSelectValue}
        title={
          <FormattedMessage
            {...messages.travelingFromTo}
            values={{
              originCity: originCity.name,
              destinationCity: destinationCity.name,
            }}
          />
        }
      />

      <Text data-testid="savedCo2">
        <FormattedMessage
          {...(savedCo2gPercentage >= 0
            ? messages.emitLessCO2
            : messages.emitMoreCO2)}
          values={{
            percentage: (
              <TextHiglight>
                {Math.abs(savedCo2gPercentage).toFixed(2)}%
              </TextHiglight>
            ),
          }}
        />
      </Text>

      <EtiquetteWrapper>
        <Etiquette
          animated={true}
          comparisons={shuffle([
            'veloelectrique',
            'eaudurobinet',
            'biere',
            'cafe',
            'repasavecduboeuf',
            'repasvegetarien',
            'fraise',
            'choudebruxelles',
            'aubergine',
            'avocat',
            'pasteque',
            'jeans',
            'tshirtencoton',
            'chauffageelectrique',
          ])}
          value={Math.abs(savedCo2g)}
        />
      </EtiquetteWrapper>

      <BoxActions
        content={<FormattedMessage {...messages.shareResults} />}
        shareData={{
          text: intl.formatMessage(messages['carbonEmissionTip']),
        }}
        title={<FormattedMessage {...messages.shareGoodNews} />}
      />
    </div>
  );
};

const ReduceEmissionsTabIntl = injectIntl(ReduceEmissionsTab);

export default ReduceEmissionsTabIntl;
